"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { InfoIcon, RefreshCwIcon } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { useTranslations } from "@/i18n";
import { Button } from "@ibada/ui";
import { useIsMobile } from "@/shared/hooks/use-mobile";

// Service worker update behavior: poll for a new build (on startup, every few minutes, and when the tab
// refocuses) and always surface the **prompt** so the user chooses when to refresh — in every environment,
// including local dev. Dev just polls a little faster while iterating.
const IS_PROD = import.meta.env.PROD;
const UPDATE_INTERVAL_MS = (IS_PROD ? 5 : 1) * 60 * 1000;
const VISIBILITY_THROTTLE_MS = 60 * 1000;

export function PwaUpdatePrompt() {
  const t = useTranslations("common.pwaUpdate");

  // Capture the SW registration handed to us by vite-plugin-pwa so we can call
  // `registration.update()` on our own schedule. Stored in a ref so the
  // visibility/interval handlers always see the latest value without
  // re-subscribing.
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  // Tracks the last time we asked the SW to check for an update. The
  // visibility handler consults this to throttle rapid tab switches.
  const lastCheckRef = useRef<number>(0);

  const requestUpdateCheck = useCallback(async () => {
    const registration = registrationRef.current;
    if (!registration) return;
    lastCheckRef.current = Date.now();
    try {
      await registration.update();
    } catch {
      // Network failures or offline state are fine — the next scheduled
      // tick will retry. Surfacing an error here would only spam logs.
    }
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      // Startup check: kick a check now (the plugin's own initial registration
      // doesn't always count as an update probe in long-lived sessions).
      void requestUpdateCheck();
    },
  });

  // Visibility-based check: probe for a new SW whenever the tab comes back to
  // the foreground, but no more than once per minute so flipping between tabs
  // doesn't hammer the server.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastCheckRef.current < VISIBILITY_THROTTLE_MS) return;
      void requestUpdateCheck();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [requestUpdateCheck]);

  // 15-minute heartbeat. The interval is cleared on unmount so unmount/remount
  // cycles (e.g. router transitions that re-mount this component) don't leak
  // timers.
  useEffect(() => {
    const id = window.setInterval(() => {
      void requestUpdateCheck();
    }, UPDATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [requestUpdateCheck]);

  // Local dismissal flag. Reset every time the SW reports a fresh update
  // so that closing the prompt with "Later" doesn't hide subsequent updates
  // detected during the same session.
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (needRefresh) setDismissed(false);
  }, [needRefresh]);

  // Always prompt — the user decides when to refresh onto the new build.
  if (!needRefresh || dismissed) return null;

  if (isMobile) {
    // Phone: a compact card floating just above the tab bar (never over it), in the app's phone style: a green
    // icon tile, the title and one line, then Later / Refresh as pills.
    return (
      <div
        role='dialog'
        aria-live='polite'
        aria-label={t("title")}
        className='fixed inset-x-4 z-[100] flex flex-col gap-3.5 rounded-[1.5rem] bg-card p-4 text-card-foreground shadow-[0_12px_32px_-12px_rgba(16,24,40,0.35)] ring-1 ring-border/50'
        style={{ bottom: "calc(6.25rem + env(safe-area-inset-bottom))" }}
      >
        <div className='flex items-center gap-3'>
          <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/12 text-primary'>
            <RefreshCwIcon aria-hidden className='size-5' />
          </span>
          <span className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-base font-extrabold'>{t("title")}</span>
            <span className='text-[13px] leading-snug font-medium text-muted-foreground'>{t("description")}</span>
          </span>
        </div>
        <div className='grid grid-cols-2 gap-2.5'>
          <button
            type='button'
            onClick={() => setDismissed(true)}
            className='h-11 rounded-full bg-muted text-[15px] font-extrabold text-foreground active:brightness-95'
          >
            {t("later")}
          </button>
          <button
            type='button'
            onClick={() => {
              void updateServiceWorker(true);
            }}
            className='h-11 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground active:brightness-95'
          >
            {t("refresh")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role='dialog'
      aria-live='polite'
      aria-label={t("title")}
      className='border-border bg-popover text-popover-foreground fixed right-4 bottom-6 left-4 z-[100] flex w-auto max-w-[min(100vw-2rem,28rem)] flex-col gap-3 rounded-2xl border p-4 shadow-[var(--shadow-floating)] sm:right-6 sm:bottom-8 sm:left-auto'
    >
      <div className='flex items-start gap-3'>
        <div className='bg-muted/60 text-muted-foreground border-border/70 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border'>
          <InfoIcon className='size-4' />
        </div>
        <div className='min-w-0 space-y-1'>
          <p className='text-sm font-semibold sm:text-base'>{t("title")}</p>
          <p className='text-muted-foreground text-sm leading-6'>{t("description")}</p>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-2'>
        <Button
          type='button'
          variant='secondary'
          className='w-full'
          onClick={() => setDismissed(true)}
        >
          {t("later")}
        </Button>
        <Button
          type='button'
          className='w-full'
          onClick={() => {
            void updateServiceWorker(true);
          }}
        >
          {t("refresh")}
        </Button>
      </div>
    </div>
  );
}
